import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, ResponsiveContent } from '../ui/ResponsiveLayout';
import BrandSymbol from '../ui/BrandSymbol';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { settingsService, type AccountDeletionStatus } from '../../services/settingsService';
import { formatPhoneNumberOnly, PHONE_MAX_LENGTH, PHONE_PLACEHOLDER, COUNTRY_CODES, parsePhoneNumber } from '../../utils/phoneFormat';
import PasswordChangeModal from '../ui/PasswordChangeModal';
import DeleteAccountModal from '../ui/DeleteAccountModal';
import TutorialTooltip from '../ui/TutorialTooltip';
import EstateCard from '../ui/EstateCard';
import { useRole } from '../../contexts/RoleContext';
import { getDashboardRouteForRole } from '../../utils/getDashboardRouteForRole';

import { API_BASE_URL } from '../../config/api';

interface ExecutorEstate {
  estateOwnerId: string;
  ownerName: string;
  ownerEmail: string;
  inviteStatus: string;
  hasApprovedDeathCert: boolean;
}

interface BeneficiaryEstate {
  estateOwnerId: string;
  ownerName: string;
  ownerEmail: string;
  inviteStatus: string;
}



const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { activeRole, switchRole } = useRole();
  const dashboardRoute = getDashboardRouteForRole(activeRole);
  const { session, updateUserInfo, updateTaskProgress, isLoading } = useSessionPersistence();
  
  // Form state
  const [name, setName] = useState(session.name || '');
  const [email, setEmail] = useState(session.email || '');
  const [countryCode, setCountryCode] = useState('+1');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [roles, setRoles] = useState<string[]>(['Planning My Own Estate']); // Default role
  const [primaryEstate] = useState('My Estate');
  const [notifications, setNotifications] = useState({
    email: true, // Default ON
    inApp: false,
    sms: false
  });

  const [deletionStatus, setDeletionStatus] = useState<AccountDeletionStatus | null>(null);

  // Executor estates state
  const [executorEstates, setExecutorEstates] = useState<ExecutorEstate[]>([]);
  const [beneficiaryEstates, setBeneficiaryEstates] = useState<BeneficiaryEstate[]>([]);
  const [estatesLoading, setEstatesLoading] = useState(true);

  // Tutorial ref for positioning
  const estateSectionRef = useRef<HTMLDivElement>(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Phone formatting is now handled by shared utility in utils/phoneFormat.ts

  // Load initial settings when session data becomes available
  useEffect(() => {
    // Only update when session is loaded and has data
    if (!isLoading && session) {
      // Get phone from taskProgress (where Frame133 saves) or session.phone (where SettingsPage saves)
      const aboutData = session.taskProgress?.about?.answers || {};
      const phoneValue = aboutData.phone || session.phone;
      
      console.log('[SettingsPage] Updating form fields from session:', {
        name: session.name,
        email: session.email,
        sessionPhone: session.phone,
        aboutPhone: aboutData.phone,
        phoneValue
      });

      setName(session.name || '');
      setEmail(session.email || '');

      // Parse existing phone number from either location
      if (phoneValue) {
        const parsed = parsePhoneNumber(phoneValue);
        console.log('[SettingsPage] Parsed phone:', parsed);
        setCountryCode(parsed.countryCode);
        setPhoneNumber(parsed.phoneNumber);
      }

      setNotifications(session.notifications || {
        email: true, // Default ON
        inApp: false,
        sms: false
      });
    }
  }, [isLoading, session.name, session.email, session.phone, session.taskProgress?.about?.answers, session.notifications]);

  // Fetch user roles and executor estates from backend on mount
  useEffect(() => {
    const fetchRolesAndEstates = async () => {
      try {
        const deletionStatus = await settingsService.getDeletionStatus();
        if (deletionStatus.success) {
          setDeletionStatus(deletionStatus);
        }

        const token = localStorage.getItem('account-token');
        if (!token) {
          setEstatesLoading(false);
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/user/roles`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            if (data.roles) {
              setRoles(data.roles);
              console.log('[SettingsPage] Loaded user roles:', data.roles);
            }
            if (data.executorFor && data.executorFor.length > 0) {
              setExecutorEstates(data.executorFor);
              console.log('[SettingsPage] Loaded executor estates:', data.executorFor);
            }
            if (data.beneficiaryFor && data.beneficiaryFor.length > 0) {
              setBeneficiaryEstates(data.beneficiaryFor);
              console.log('[SettingsPage] Loaded beneficiary estates:', data.beneficiaryFor);
            }
          }
        }
      } catch (err) {
        console.error('[SettingsPage] Failed to fetch roles:', err);
        // Keep default role on error
      } finally {
        setEstatesLoading(false);
      }
    };

    fetchRolesAndEstates();
  }, []); // Only fetch on mount

  // Save settings
  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Combine country code + phone number for storage
      const digits = phoneNumber.replace(/\D/g, '');
      const fullPhone = digits ? `${countryCode}${digits}` : '';

      const response = await settingsService.updateProfile({
        name,
        email,
        phone: fullPhone,
        // roles removed - read-only field
        primaryEstate,
        notifications
      });

      if (response.success) {
        setSuccess('Settings saved successfully');

        // Update session with new values (excluding read-only roles)
        await updateUserInfo({
          name,
          email,
          phone: fullPhone,
          notifications
        });
        
        // Also update taskProgress.about.answers.phone to keep both in sync
        // (Frame133 saves to taskProgress, SettingsPage saves to session.phone)
        updateTaskProgress('about', { phone: fullPhone });
      } else {
        setError(response.message || 'Failed to save settings');
      }
    } catch (err) {
      setError('An error occurred while saving settings');
      console.error('Settings save error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Update password
  const handlePasswordChange = async (currentPassword: string, newPassword: string) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await settingsService.updatePassword(currentPassword, newPassword);
      if (response.success) {
        setSuccess('Password updated successfully');
        setShowPasswordModal(false);
      } else {
        setError(response.message || 'Failed to update password');
      }
    } catch (err) {
      setError('An error occurred while updating password');
      console.error('Password update error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Update notifications
  const handleNotificationChange = async (type: keyof typeof notifications, value: boolean) => {
    const newNotifications = { ...notifications, [type]: value };
    setNotifications(newNotifications);

    try {
      await settingsService.updateNotifications(newNotifications);
    } catch (err) {
      // Revert on error
      setNotifications(notifications);
      setError('Failed to update notification preferences');
      console.error('Notification update error:', err);
    }
  };

  // Download data
  const handleDownloadData = async () => {
    try {
      const blob = await settingsService.downloadData();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'my-estate-data.json';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download data');
      console.error('Data download error:', err);
    }
  };

  // Delete account - just navigate to animation page
  const handleDeleteAccount = async () => {
    // Close the modal
    setShowDeleteConfirm(false);

    // Navigate to animation page (which handles account deletion + logout after 10s)
    if( deletionStatus?.canDeleteFullAccount || deletionStatus?.canDeletePlannerProfile ) {
      navigate('/deleteaccount');
    }
  };

  return (
    <ResponsiveContainer background="stone">
      <ResponsiveContent>
        {/* Header */}
   <div className="flex flex-col items-start mb-4 w-full max-w-3xl mx-auto">
          {/* Back Button Wrapper */}
          <div className="w-full relative z-10">
            <button
              type="button"
              onClick={() => navigate(dashboardRoute)}
              className="relative z-10 inline-flex items-center gap-2 px-1 py-2 text-cyan-700 hover:text-cyan-800 text-sm font-medium"
            >
              ← Back to Dashboard
            </button>
          </div>

          <div className="flex flex-col items-center justify-center w-full mt-2 mb-2">
            <BrandSymbol size="4xl" variant="dark" showText={true} />
          </div>

          <div className="w-full text-center">
            <h1 className="font-display text-2xl font-semibold text-stone-800 m-0 leading-tight">
              Settings
            </h1>
            <p className="text-stone-600 m-0 leading-normal">
              Manage your personal details, preferences, and how End of an Era supports you.
            </p>
          </div>
        </div>

        {/* Settings Sections */}
        <div className="max-w-3xl mx-auto space-y-8">
          {/* 1. Account Details */}
          <section className="bg-white rounded-lg shadow-sm p-2">
            <h2 className="text-xl font-semibold text-stone-800 mb-4">Account Details</h2>
            <p className="text-sm text-stone-600 mb-4">Keep your account info up to date.</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Full Name</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={loading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Email Address</label>
                <input
                  type="email"
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Phone Number</label>
                <div className="flex gap-2">
                  {/* Country Code Dropdown */}
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="w-24 px-2 py-2 border border-stone-300 rounded-md"
                    disabled={loading}
                  >
                    {COUNTRY_CODES.map((cc) => (
                      <option key={cc.code} value={cc.code}>
                        {cc.code}
                      </option>
                    ))}
                  </select>

                  {/* Phone Number Input */}
                  <input
                    type="tel"
                    className="flex-1 px-3 py-2 border border-stone-300 rounded-md"
                    placeholder={PHONE_PLACEHOLDER}
                    value={formatPhoneNumberOnly(phoneNumber)}
                    onChange={(e) => {
                      // Extract only digits from user input
                      const digits = e.target.value.replace(/\D/g, '');
                      // Store raw digits in state (no formatting)
                      setPhoneNumber(digits);
                    }}
                    maxLength={PHONE_MAX_LENGTH}
                    disabled={loading}
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium text-stone-700">Password</label>
                  <span className="text-sm text-stone-500">••••••••</span>
                </div>
                <button
                  onClick={() => setShowPasswordModal(true)}
                  className="text-cyan-700 hover:text-cyan-800 text-sm font-medium"
                  disabled={loading}
                >
                  Change Password
                </button>
              </div>

              {/* MFA Hidden until implemented */}
              {false && <div className="flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium text-stone-700">Two-Factor Authentication</label>
                  <span className="text-sm text-stone-500">Not enabled</span>
                </div>
                <button
                  className="text-cyan-700 hover:text-cyan-800 text-sm font-medium"
                  disabled={loading}
                >
                  Enable
                </button>
              </div>}

              {/* Connected Devices Hidden until implemented */}
              {false && <div className="flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium text-stone-700">Connected Devices</label>
                  <span className="text-sm text-stone-500">1 active device</span>
                </div>
                <button
                  className="text-cyan-700 hover:text-cyan-800 text-sm font-medium"
                  disabled={loading}
                >
                  View and manage
                </button>
              </div>}

              {/* Save Button */}
              <div className="mt-6 flex items-center justify-end gap-4">
                {error && (
                  <p className="text-sm text-red-600">{error}</p>
                )}
                {success && (
                  <p className="text-sm text-green-600">{success}</p>
                )}
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className={`px-4 py-2 rounded text-white font-medium ${
                    loading ? 'bg-cyan-500 cursor-not-allowed' : 'bg-cyan-700 hover:bg-cyan-800'
                  }`}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </section>

          {/* 2. Estate Selection Hub */}
          <section className="bg-white rounded-lg shadow-sm p-6 relative" ref={estateSectionRef}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold text-stone-800">Your Estates</h2>
                <p className="text-sm text-stone-600">Manage your own estate plan or estates you're an executor for.</p>
              </div>
            </div>

            {/* Tutorial Tooltip */}
            {executorEstates.length > 0 && estateSectionRef.current && (
              <TutorialTooltip
                tutorialId="executor_welcome"
                title="Welcome, Executor!"
                content={
                  <div>
                    <p className="mb-2">You've been designated as an executor for one or more estates.</p>
                    <p className="mb-2">
                      <strong>Your Estate Plan:</strong> Click to continue planning your own legacy.
                    </p>
                    <p>
                      <strong>Estates You Manage:</strong> Click to view the estate, upload a death certificate, or begin the settlement process.
                    </p>
                  </div>
                }
                targetRef={estateSectionRef}
                position="bottom"
              />
            )}

            <div className="space-y-4">
              {/* My Estate Plan */}
              <div>
                <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wide mb-3">My Estate Plan</h3>
                <EstateCard
                  type="own"
                  estateName="My Estate Plan"
                  status="active"
                  onClick={() => navigate('/frame-140')}
                />
              </div>

              {/* Estates I Manage as Executor */}
              {executorEstates.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wide mb-3">
                    Estates I Manage ({executorEstates.length})
                  </h3>
                  <div className="space-y-3">
                    {executorEstates.map((estate) => (
                      <EstateCard
                        key={estate.estateOwnerId}
                        type="executor"
                        estateName={`${estate.ownerName}'s Estate`}
                        ownerEmail={estate.ownerEmail}
                        status={
                          estate.hasApprovedDeathCert 
                            ? 'approved' 
                            : estate.inviteStatus === 'accepted' 
                              ? 'no-cert' 
                              : 'pending'
                        }
                        onClick={() => {
                          switchRole({
                            role: 'executor',
                            estateOwnerId: estate.estateOwnerId,
                            estateOwnerName: estate.ownerName,
                            estateOwnerEmail: estate.ownerEmail,
                            inviteStatus: estate.inviteStatus,
                            hasApprovedDeathCert: estate.hasApprovedDeathCert,
                          });

                          navigate(`/executor-plan/${estate.estateOwnerId}/eframe-141`, {
                            state: { ownerName: estate.ownerName },
                          });
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Loading state */}
              {estatesLoading && (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-700 mx-auto"></div>
                  <p className="text-sm text-stone-500 mt-2">Loading estates...</p>
                </div>
              )}

              {/* Beneficiary Estates */}
              {beneficiaryEstates.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wide mb-3">
                    Estates I'm a Beneficiary Of ({beneficiaryEstates.length})
                  </h3>
                  <div className="space-y-3">
                    {beneficiaryEstates.map((estate) => (
                      <button
                        key={estate.estateOwnerId}
                        onClick={() => navigate(`/beneficiary-coming-soon/${estate.estateOwnerId}`, { state: { ownerName: estate.ownerName } })}
                        className="w-full text-left bg-white rounded-xl shadow-sm border border-stone-200 hover:border-purple-300 hover:shadow-md transition-all p-5"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-6 h-6 text-purple-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 01-8 0M12 14v7m-7-3a7 7 0 0114 0" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <h3 className="font-semibold text-stone-800 truncate">{estate.ownerName}'s Estate</h3>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 flex-shrink-0">
                                Beneficiary
                              </span>
                            </div>
                            <p className="text-sm text-cyan-700 font-medium mt-2">View beneficiary access →</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* No executor estates message — only show if also not a beneficiary */}
              {!estatesLoading && executorEstates.length === 0 && beneficiaryEstates.length === 0 && (
                <div className="bg-stone-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-stone-600">
                    You haven't been designated as an executor or beneficiary for any estates yet.
                  </p>
                </div>
              )}
            </div>

            {/* Roles summary */}
            <div className="mt-6 pt-4 border-t border-stone-200">
              <p className="text-xs text-stone-500">
                Your roles: {roles.join(' • ')}
              </p>
            </div>
          </section>

          {/* 3. Billing & Subscription */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-stone-800 mb-4">Billing & Subscription</h2>
            <p className="text-sm text-stone-600 mb-4">Manage your End of an Era subscription and billing details.</p>
            
            <div className="space-y-4">
              <a
                href="https://billing.stripe.com/p/login/eVqeV62xedtAdQX1YEbjW00"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-700 hover:bg-cyan-800 text-white rounded-lg font-medium transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                Manage Subscription
              </a>
              <p className="text-xs text-stone-500">
                Opens Stripe Customer Portal in a new tab
              </p>
            </div>
          </section>

          {/* 4. Notification Preferences */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-stone-800 mb-4">Notification Preferences</h2>
            <p className="text-sm text-stone-600 mb-4">Choose when and how we keep you in the loop.</p>

            <div className="space-y-4">
              {[
                { key: 'email', label: 'Email Notifications' }
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center justify-between">
                  <label className="text-sm font-medium text-stone-700">{label}</label>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name={key}
                        className="text-cyan-700 focus:ring-cyan-700"
                        checked={notifications[key as keyof typeof notifications]}
                        onChange={() => handleNotificationChange(key as keyof typeof notifications, true)}
                        disabled={loading}
                      />
                      <span className="ml-2 text-sm text-stone-700">On</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name={key}
                        className="text-cyan-700 focus:ring-cyan-700"
                        checked={!notifications[key as keyof typeof notifications]}
                        onChange={() => handleNotificationChange(key as keyof typeof notifications, false)}
                        disabled={loading}
                      />
                      <span className="ml-2 text-sm text-stone-700">Off</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* 5. Privacy & Data */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-stone-800 mb-4">Privacy & Data</h2>
            <p className="text-sm text-stone-600 mb-4">You're in control of your information.</p>
            
            <div className="space-y-4">
              {/* Hidden buttons until implemented */}
              {false && <>
                <button
                  onClick={handleDownloadData}
                  disabled={loading}
                  className="w-full text-left px-4 py-2 border border-stone-300 rounded-md hover:bg-stone-50 text-sm font-medium text-stone-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Download My Data
                </button>
                <button
                  disabled={loading}
                  className="w-full text-left px-4 py-2 border border-stone-300 rounded-md hover:bg-stone-50 text-sm font-medium text-stone-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Manage Shared Access
                </button>
                <button
                  disabled={loading}
                  className="w-full text-left px-4 py-2 border border-stone-300 rounded-md hover:bg-stone-50 text-sm font-medium text-stone-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Trusted Contacts & Delegates
                </button>
              </>}
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={loading}
                className="w-full text-left px-4 py-2 border border-red-300 rounded-md hover:bg-red-50 text-sm font-medium text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                { deletionStatus?.canDeleteFullAccount ? 'Delete My Account' : deletionStatus?.canDeletePlannerProfile ? 'Delete My Planner Profile' : 'No account deletion options available' }
              </button>
            </div>
          </section>

          {/* 6. Support & Feedback */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-stone-800 mb-4">Support & Feedback</h2>
            <p className="text-sm text-stone-600 mb-4">Get help or help us improve.</p>
            
            <div className="space-y-4">
              <button
                onClick={() => navigate('/frame-199')}
                disabled={loading}
                className="w-full text-left px-4 py-2 border border-stone-300 rounded-md hover:bg-stone-50 text-sm font-medium text-stone-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Visit Help Center
              </button>
              <button
                onClick={() => navigate('/frame-199')}
                disabled={loading}
                className="w-full text-left px-4 py-2 border border-stone-300 rounded-md hover:bg-stone-50 text-sm font-medium text-stone-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Contact Support
              </button>
              <button
                onClick={() => navigate('/frame-199')}
                disabled={loading}
                className="w-full text-left px-4 py-2 border border-stone-300 rounded-md hover:bg-stone-50 text-sm font-medium text-stone-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Report a Bug
              </button>
              <button
                onClick={() => navigate('/frame-199')}
                disabled={loading}
                className="w-full text-left px-4 py-2 border border-stone-300 rounded-md hover:bg-stone-50 text-sm font-medium text-stone-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Leave Feedback
              </button>
            </div>
          </section>
        </div>
      </ResponsiveContent>

      {/* Password Change Modal */}
      <PasswordChangeModal
        open={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onSubmit={handlePasswordChange}
        loading={loading}
      />

      {/* Delete Account Modal */}
      <DeleteAccountModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteAccount}
        loading={loading}
      />
    </ResponsiveContainer>
  );
};

export default SettingsPage;
